import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {result} from 'lodash';
import {Transition} from 'react-transition-group';
import {FEATURE_FLAGS, onFlagChange} from '../../../services/feature_flags';
import {
    createPipelineStep,
    currentPipeline,
    getDefaultResultsTableStyles,
    getResultsTableStyles,
} from '../../../utils/pipeline_utils';
import ResultsTable from '../../common/results_table';
import TableMenu from "./table_actions/menu";

export default class BasePipelineStepComponent extends Component {
    icon = null;

    constructor(props) {
        super(props);

        const tempStep = createPipelineStep(props.step.type, props.step);
        const results = currentPipeline.getResultForStep(tempStep) || {};
        this.state = {
            editing: results.incomplete || results.error,
            transitionDuration: FEATURE_FLAGS.STEP_TRANSITION_DURATION,
            tempStep,
            results,
        };

        this.onChange = this.onChange.bind(this);
        this.onSave = this.onSave.bind(this);
        this.onCancel = this.onCancel.bind(this);
    }

    componentDidUpdate(prevProps, prevState) {
        const {step, open} = this.props;
        let {results = {}} = this.state;
        const stepResults = currentPipeline.getResultForStep(step);
        if (prevProps.step !== step) {
            this.setState({
                tempStep: createPipelineStep(step.type, step),
                results: stepResults,
            });
        }

        // checking for case where step was deleted
        // assuming stepResults has table if it is defined
        if (stepResults && results.table && stepResults.table.id !== results.table.id) {
            this.setState({results: stepResults});
        }

        if (!prevProps.open && open && (results.incomplete || results.error)) {
            this.setState({editing: true}, () => { this.focusInput(); });
        } else if (prevProps.open && !open) {
            this.setState({editing: false})
        }
    }

    componentDidMount() {
        this.focusInput();
        this.unbindTransitionDurationListener = onFlagChange('STEP_TRANSITION_DURATION', () => { this.setState({transitionDuration: FEATURE_FLAGS.STEP_TRANSITION_DURATION})});
    }

    componentWillUnmount() {
        this.unbindTransitionDurationListener();
    }

    /**
     * Overwrite if needing to do dynamic setting of the icon
     * @returns {*}
     */
    getIcon() {
        return this.icon;
    }

    /**
     * Set a ref to stepInput if wanting to autofocus when opening the step
     */
    focusInput() {
        if (this.stepInput) {
            this.stepInput.focus();
        }
    }

    onSave(ev) {
        ev.preventDefault();
        const {results} = this.state;
        if (!results.incomplete && !results.error) {
            this.state.tempStep.markSaved();
            this.props.updateStep(this.state.tempStep);
            this.setState({editing: false});
        }
    }

    reset() {
        const {step} = this.props;
        const results = currentPipeline.getResultForStep(step);
        this.setState({
            tempStep: createPipelineStep(step.type, step),
            results,
            editing: false,
        });
    }

    onCancel(ev) {
        const {step, onRemove} = this.props;
        ev.preventDefault();
        step.isNew() ? onRemove(step) : this.reset();
    }

    onChange(ev) {
        const {step} = this.props;
        const {tempStep} = this.state;
        const newStep = createPipelineStep(tempStep.type, tempStep);
        this.updateStep(ev, newStep);
        const results = newStep.apply(currentPipeline.getInputTableForStep(step));
        return new Promise((resolve) => {
            this.setState({tempStep: newStep, results}, resolve);
        });
    }

    canEdit() {
        return true;
    }

    renderHeader() {
        return null;
    }

    renderForm() {
        return null;
    }

    /**
     * Render the value when the step is closed
     * @returns {null}
     */
    renderValue() {
        return null;
    }

    renderStepContent() {
        const {editing, results} = this.state;
        const {open, step, toggleOpen} = this.props;
        return (
            <>
                <div className="pipeline-step-header-text">
                    <img src={`images/${this.getIcon()}.svg`} alt=""/>
                    {(editing || results.incomplete || results.error) ?
                        this.renderHeader() :
                        this.renderValue()
                    }
                </div>
                <div className="pipeline-step-header-actions">
                    {open && !editing && this.canEdit() && (
                        <button
                            className="edit-button button"
                            onClick={() => { this.setState({editing: true}); }}
                        >
                            Edit
                        </button>
                    )}
                    {open &&
                        <button
                            className="delete-button button"
                            onClick={() => { this.props.onRemove(this.props.step); }}
                        >
                            Remove Step
                        </button>
                    }
                    {!open && (
                        <button
                            className="open-toggle"
                            onClick={() => { toggleOpen(step); }}
                        >
                            <img src="images/down-chevron.svg" alt="" width="20" height="20" />
                        </button>
                    )}
                </div>
            </>
        )
    }

    render() {
        const {step, showTable, open, adjacentToSelected} = this.props;
        const {editing, results, transitionDuration} = this.state;

        let classes = `pipeline-step pipeline-step-${open ? 'open' : 'closed'} pipeline-step-${step.type}`;
        if (adjacentToSelected) {
            classes += ' pipeline-step-adjacent';
        }
        if (results.incomplete) {
            classes += ' incomplete';
        }
        if (results.error) {
            classes += ' error';
        }

        const shouldShowTable = showTable && !!result(results, 'table');

        return (
            <>
                <div
                    className={classes}
                >
                    <div className="pipeline-step-header">
                        {this.renderStepContent()}
                    </div>
                    {open && editing && (
                        <>
                            <div className="pipeline-step-form">
                                {this.renderForm()}
                            </div>
                            <div className="pipeline-step-form-footer">
                                <button
                                    className="button secondary-button"
                                    onClick={this.onCancel}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="button submit-button"
                                    disabled={results.incomplete || results.error}
                                    onClick={this.onSave}
                                >
                                    Apply
                                </button>
                            </div>
                        </>
                    )}

                    {open && FEATURE_FLAGS.TABLE_MENU_SIDE ? <TableMenu step={step} /> : null}

                    <Transition
                        in={shouldShowTable}
                        timeout={transitionDuration}
                    >
                        {(state) => (
                            result(results, 'table') && (
                            <div
                                className="pipeline-step-body"
                                style={{
                                    ...getDefaultResultsTableStyles(transitionDuration),
                                    ...getResultsTableStyles(350)[state]
                                }}
                            >
                                <div className="pipeline-step-results">
                                    {editing && <label className="label">PREVIEW</label>}
                                    <ResultsTable
                                        data={results.table.getSampleData(false)}
                                        step={step}
                                        zebraStripes={false}
                                        editingPipelineStep={editing}
                                        showMenu={!editing}
                                        showTableMenu={false}
                                        limit={5}
                                    />
                                </div>
                            </div>
                        ))}
                    </Transition>
                </div>
            </>
        );
    }
}

BasePipelineStepComponent.propTypes = {
    open: PropTypes.bool,
    showTable: PropTypes.bool,
    adjacentToSelected: PropTypes.bool,
    updateStep: PropTypes.func.isRequired,
    toggleOpen: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
};

BasePipelineStepComponent.defaultProps = {
    open: false,
    showTable: false,
    adjacentToSelected: false,
};
